/**
 * ResourcesList - Recreation of Base44 ResourcesList Component
 * Enhanced resource table with advanced features
 * 
 * @description Professional resource listing with status indicators,
 * health monitoring, and basic table functionality (simplified for Phase 2)
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Server, 
  Database, 
  HardDrive, 
  Network,
  MoreHorizontal,
  ExternalLink
} from 'lucide-react';

// TypeScript interfaces for type safety
interface CloudResource {
  id: string;
  name: string;
  type: 'compute' | 'storage' | 'database' | 'network';
  provider: 'aws' | 'gcp' | 'azure';
  status: 'running' | 'stopped' | 'error' | 'pending';
  health: 'healthy' | 'warning' | 'critical';
  region: string;
  cost: number;
}

interface ResourcesListProps {
  resources: CloudResource[];
  activeProvider: string;
}

// Resource type icons
const typeIcons = {
  compute: Server,
  storage: HardDrive,
  database: Database,
  network: Network
};

// Status color mapping
const statusColors = {
  running: 'bg-green-100 text-green-800 border-green-200',
  stopped: 'bg-gray-100 text-gray-800 border-gray-200',
  error: 'bg-red-100 text-red-800 border-red-200',
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200'
};

// Health color mapping
const healthColors = {
  healthy: 'bg-green-500',
  warning: 'bg-yellow-500',
  critical: 'bg-red-500'
};

export function ResourcesList({ resources, activeProvider }: ResourcesListProps) {
  // Filter resources by active provider
  const filteredResources = resources.filter(r => r.provider === activeProvider);

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold">Recent Resources</CardTitle>
          <Badge variant="outline" className="text-xs">
            {filteredResources.length} resources
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent>
        {filteredResources.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No resources found for {activeProvider.toUpperCase()}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredResources.slice(0, 5).map((resource) => {
              const Icon = typeIcons[resource.type];
              
              return (
                <div 
                  key={resource.id}
                  className="flex items-center gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                >
                  {/* Resource type icon */}
                  <div className="flex-shrink-0">
                    <div className="p-2 rounded-lg bg-muted">
                      <Icon className="w-4 h-4" />
                    </div>
                  </div>
                  
                  {/* Resource details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium truncate">{resource.name}</h4>
                      {/* Health indicator */}
                      <div className={`w-2 h-2 rounded-full ${healthColors[resource.health]}`} />
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="capitalize">{resource.type}</span>
                      <span>•</span>
                      <span>{resource.region}</span>
                      <span>•</span>
                      <span>{formatCurrency(resource.cost)}/month</span>
                    </div>
                  </div>
                  
                  {/* Status badge */}
                  <div className="flex-shrink-0">
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${statusColors[resource.status]}`}
                    >
                      {resource.status}
                    </Badge>
                  </div>
                  
                  {/* Actions menu */}
                  <div className="flex-shrink-0">
                    <div className="p-2 rounded-lg hover:bg-muted cursor-pointer">
                      <MoreHorizontal className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* View all link */}
            {filteredResources.length > 5 && (
              <div className="text-center pt-4 border-t">
                <div className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground cursor-pointer">
                  <span>View all {filteredResources.length} resources</span>
                  <ExternalLink className="w-3 h-3" />
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ResourcesList;